import React, { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Textarea } from "@nextui-org/react";
import toast from "react-hot-toast";
import { ExclamationTriangleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { postWithFormDataFetcher } from "../../../../services/api";
import getErrorMessage from "../../../../services/errorMessage";

const BATAS_BERKAS = 2 * 1024 * 1024;
const TIPE_DIIZINKAN = ["application/pdf", "image/jpeg", "image/png"];

/**
 * Form Penghapusan Kendaraan - dua langkah.
 *
 * Langkah pertama mengisi alasan dan surat keterangan; langkah kedua
 * mengonfirmasi. Sengaja dipisah: penghapusan kendaraan tidak bisa dibatalkan
 * dari layar mana pun, jadi tekanan terakhirnya harus disengaja, bukan
 * kelanjutan refleks dari mengisi formulir.
 *
 * Identitas kendaraan ditampilkan sebagai teks mati - bukan isian - karena yang
 * dihapus harus persis kendaraan yang terlihat di baris tabel tadi.
 */
export default function ModalHapusKendaraan({ isOpen, onOpenChange, onClose, kendaraan, onSuccess }) {
  const [alasan, setAlasan] = useState("");
  const [berkas, setBerkas] = useState(null);
  const [tahapKonfirmasi, setTahapKonfirmasi] = useState(false);
  const [sedangMengirim, setSedangMengirim] = useState(false);

  // Modal NextUI tetap terpasang saat tertutup, jadi isian dibersihkan sendiri
  // setiap kali dibuka - kalau tidak, alasan kendaraan sebelumnya ikut terbawa.
  useEffect(() => {
    if (isOpen) {
      setAlasan("");
      setBerkas(null);
      setTahapKonfirmasi(false);
    }
  }, [isOpen]);

  function pilihBerkas(event) {
    const dipilih = event.target.files?.[0];
    if (!dipilih) {
      setBerkas(null);
      return;
    }

    if (!TIPE_DIIZINKAN.includes(dipilih.type)) {
      toast.error("Surat keterangan harus berupa PDF, JPG, atau PNG.");
      event.target.value = "";
      return;
    }

    if (dipilih.size > BATAS_BERKAS) {
      toast.error("Ukuran surat keterangan melebihi 2 MB.");
      event.target.value = "";
      return;
    }

    setBerkas(dipilih);
  }

  function lanjutKeKonfirmasi() {
    if (alasan.trim().length < 5) {
      toast.error("Alasan penghapusan harus diisi, minimal 5 karakter.");
      return;
    }

    if (!berkas) {
      toast.error("Surat keterangan penghapusan wajib diunggah.");
      return;
    }

    setTahapKonfirmasi(true);
  }

  async function hapusKendaraan() {
    try {
      setSedangMengirim(true);

      const muatan = new FormData();
      muatan.append("vehicleId", kendaraan?.id ?? "");
      muatan.append("alasan", alasan.trim());
      muatan.append("surat_penghapusan", berkas);

      await postWithFormDataFetcher("/api/vehicles/hapus", muatan);

      toast.success("Kendaraan dihapus dan penghapusannya tercatat.");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menghapus kendaraan"));
      setTahapKonfirmasi(false);
    } finally {
      setSedangMengirim(false);
    }
  }

  return (
    <Modal size="2xl" scrollBehavior="inside" isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} isDismissable={false}>
      <ModalContent>
        <ModalHeader>{tahapKonfirmasi ? "Konfirmasi Penghapusan Kendaraan" : "Form Penghapusan Kendaraan"}</ModalHeader>

        <ModalBody>
          <div className="rounded-md border border-line-strong p-3 text-sm">
            <p className="font-semibold mb-2">Kendaraan yang akan dihapus</p>
            <div className="grid grid-cols-3 gap-y-1">
              <span className="text-ink-500">Perusahaan</span>
              <span className="col-span-2">: {kendaraan?.company?.name || "-"}</span>
              <span className="text-ink-500">No. Polisi</span>
              <span className="col-span-2">: {kendaraan?.noPolisi || "-"}</span>
              <span className="text-ink-500">No. Mesin</span>
              <span className="col-span-2">: {kendaraan?.nomorMesin || "-"}</span>
              <span className="text-ink-500">No. Rangka</span>
              <span className="col-span-2">: {kendaraan?.nomorRangka || "-"}</span>
            </div>
          </div>

          {!tahapKonfirmasi ? (
            <>
              <Textarea
                label="Alasan Penghapusan"
                labelPlacement="outside"
                placeholder="Jelaskan alasan kendaraan ini dihapus..."
                minRows={3}
                isRequired
                value={alasan}
                onValueChange={setAlasan}
              />

              <div>
                <label className="text-sm block mb-1" htmlFor="surat-penghapusan">
                  Surat Keterangan Penghapusan <span className="text-danger">*</span>
                </label>
                <input
                  id="surat-penghapusan"
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={pilihBerkas}
                  className="w-full text-sm border border-line-strong rounded-md px-3 py-2 bg-white file:mr-3 file:rounded file:border-0 file:bg-surface-3 file:px-3 file:py-1 file:text-sm"
                />
                <p className="text-ink-500 text-xs mt-1">PDF, JPG, atau PNG, maksimal 2 MB.</p>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-danger-line bg-danger-bg p-3 text-sm">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="size-5 text-danger shrink-0" />
                <div>
                  <p className="font-semibold mb-1">Hapus kendaraan ini?</p>
                  <p className="mb-2">
                    Kendaraan tidak akan muncul lagi pada daftar dan tidak bisa dikembalikan dari layar ini. Penghapusan beserta alasan dan suratnya dicatat.
                  </p>
                  <p className="text-ink-700">
                    <span className="font-semibold">Alasan:</span> {alasan.trim()}
                  </p>
                  <p className="text-ink-700">
                    <span className="font-semibold">Surat:</span> {berkas?.name}
                  </p>
                </div>
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          {!tahapKonfirmasi ? (
            <>
              <Button size="sm" variant="faded" onPress={onClose}>
                Batal
              </Button>
              <Button size="sm" color="danger" onPress={lanjutKeKonfirmasi} startContent={<TrashIcon className="size-4" />}>
                Lanjut
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="faded" onPress={() => setTahapKonfirmasi(false)} isDisabled={sedangMengirim}>
                Kembali
              </Button>
              <Button size="sm" color="danger" onPress={hapusKendaraan} isLoading={sedangMengirim}>
                Ya, Hapus Kendaraan
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
